import { useState, useEffect, useContext, useCallback } from 'react';
import { AppContext } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import HubSpotService from '../services/hubspotService';

export const useHubSpot = () => {
  const { API_BASE_URL } = useContext(AppContext);
  const { token } = useAuth();
  const [hubspotService, setHubspotService] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [error, setError] = useState(null);
  const [hasMoreContacts, setHasMoreContacts] = useState(false);
  const [nextAfter, setNextAfter] = useState(null);

  // Initialize HubSpot service
  useEffect(() => {
    if (API_BASE_URL && token) {
      const service = new HubSpotService(
        API_BASE_URL,
        () => token
      );
      setHubspotService(service);
    } else {
      setHubspotService(null);
    }
  }, [API_BASE_URL, token]);

  // Fetch first page of contacts
  const fetchAllContacts = useCallback(async () => {
    if (!hubspotService) {
      return;
    }

    setLoadingContacts(true);
    setError(null);
    
    try {
      const result = await hubspotService.getFirstPageContacts(100);
      setContacts(result.contacts);
      setHasMoreContacts(result.hasMore);
      setNextAfter(result.nextAfter);
    } catch (err) {
      console.error('Error fetching HubSpot contacts:', err);
      setError(err.message);
      setContacts([]);
      setHasMoreContacts(false);
      setNextAfter(null);
    } finally {
      setLoadingContacts(false);
    }
  }, [hubspotService]);

  // Load more contacts (for pagination)
  const loadMoreContacts = useCallback(async () => {
    if (!hubspotService || !hasMoreContacts || !nextAfter) {
      return;
    }

    setLoadingContacts(true);
    
    try {
      const response = await hubspotService.getAllContacts(100, nextAfter);
      if (response.status === 'success' && response.data) {
        const newContacts = response.data.results || [];
        setContacts(prev => [...prev, ...newContacts]);
        setHasMoreContacts(!!response.data.paging?.next?.after);
        setNextAfter(response.data.paging?.next?.after);
      }
    } catch (err) {
      console.error('Error loading more contacts:', err);
      setError(err.message);
    } finally {
      setLoadingContacts(false);
    }
  }, [hubspotService, hasMoreContacts, nextAfter]);

  // Search contact by email
  const searchContact = useCallback(async (email) => {
    if (!hubspotService) return null;
    
    try {
      const result = await hubspotService.searchContact(email);
      return result;
    } catch (err) {
      console.error('Error searching contact:', err);
      throw err;
    }
  }, [hubspotService]);

  // Search contacts using the new search API
  const searchContacts = useCallback(async (query, options = {}) => {
    if (!hubspotService) return null;
    
    try {
      const result = await hubspotService.searchContacts(query, options);
      return result;
    } catch (err) {
      console.error('Error searching contacts:', err);
      throw err;
    }
  }, [hubspotService]);

  // Create new contact
  const createContact = useCallback(async (contactData) => {
    if (!hubspotService) return null;
    
    try {
      const result = await hubspotService.createContact(contactData);
      // Refresh contacts list after creating new contact
      await fetchAllContacts();
      return result;
    } catch (err) {
      console.error('Error creating contact:', err);
      throw err;
    }
  }, [hubspotService, fetchAllContacts]);

  // Find or create contact
  const findOrCreateContact = useCallback(async (contactData) => {
    if (!hubspotService) return null;
    
    try {
      const result = await hubspotService.findOrCreateContact(contactData);
      // Refresh contacts list in case a new contact was created
      await fetchAllContacts();
      return result;
    } catch (err) {
      console.error('Error finding or creating contact:', err);
      throw err;
    }
  }, [hubspotService, fetchAllContacts]);

  // Create engagement
  const createEngagement = useCallback(async (engagementData) => {
    if (!hubspotService) return null;
    
    try {
      const result = await hubspotService.createEngagement(engagementData);
      return result;
    } catch (err) {
      console.error('Error creating engagement:', err);
      throw err;
    }
  }, [hubspotService]);

  // Get contact engagements
  const getContactEngagements = useCallback(async (contactId) => {
    if (!hubspotService) return null;
    
    try {
      const result = await hubspotService.getContactEngagements(contactId);
      return result;
    } catch (err) {
      console.error('Error fetching contact engagements:', err);
      throw err;
    }
  }, [hubspotService]);

  // Get customer engagements
  const getCustomerEngagements = useCallback(async (customerId, includeDetails = true) => {
    if (!hubspotService) return null;
    
    try {
      const result = await hubspotService.getCustomerEngagements(customerId, includeDetails);
      return result;
    } catch (err) {
      console.error('Error fetching customer engagements:', err);
      throw err;
    }
  }, [hubspotService]);
  
  // Associate existing engagement with customer
  const associateEngagementWithCustomer = useCallback(async (customerId, engagementId) => {
    if (!hubspotService) return null;
    
    try {
      const result = await hubspotService.associateEngagementWithCustomer(customerId, engagementId);
      return result;
    } catch (err) {
      console.error('Error associating engagement with customer:', err);
      throw err;
    }
  }, [hubspotService]);

  return {
    // Service instance
    hubspotService,
    
    // Contacts data
    contacts,
    loadingContacts,
    error,
    hasMoreContacts,
    
    // Methods
    fetchAllContacts,
    loadMoreContacts,
    searchContact,
    searchContacts,
    createContact,
    findOrCreateContact,
    createEngagement,
    getContactEngagements,
    getCustomerEngagements,
    associateEngagementWithCustomer,
  };
};

export default useHubSpot;